import { linhaDeChecklistFeita } from '@orbind/core'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { StatusPill, initialsOf } from './bits.tsx'
import { DatesPanel } from './panels.tsx'
import type { WorkItemDetail as Detail } from './api.ts'

/**
 * RESUMO do item, no painel da lista (SPEC-20261002-1541).
 *
 * É tela PRÓPRIA, e não o detalhe completo encolhido. A primeira tentativa
 * reaproveitou `WorkItemDetailView` aqui dentro, e o resultado foi o editor
 * inteiro espremido em 420px: colunas sobrepostas, campos cortados ao meio,
 * nada legível. Um editor desenhado para a largura da tela não vira resumo só
 * por caber num lugar menor.
 *
 * O que entra é o que se quer saber ao percorrer a lista — em que pé está, de
 * quem é, para quando, e o que já andou. Mexer de verdade é na edição, a um
 * clique no código ou no "Abrir em página".
 */

type Catalogo = { id: string; code: string; tone?: string | null; nameTranslationKey?: string | null }

/** Uma escolha da lista: o que se mostra e o que se devolve ao escolher. */
type Escolha = { id: string; conteudo: ReactNode }

/**
 * Campo do resumo que mostra o valor ATUAL e abre a lista no clique
 * (SPEC-20261003-1212).
 *
 * Antes o Status desenhava todas as opções de uma vez, com a atual acesa: num
 * catálogo de quatro já tomava três linhas do painel, e lia-se como quatro
 * estados em vez de um estado com três atalhos.
 *
 * Fecha ouvindo o documento, e não com um painel invisível por cima: assim o
 * clique chega ao controle que a pessoa mirou, sem custar um segundo toque.
 */
function CampoComLista({
  atual,
  opcoes,
  onEscolher,
  vazio = '—',
  rotuloDoBotao,
}: {
  atual: ReactNode
  opcoes: Escolha[]
  onEscolher: (id: string) => void
  vazio?: string
  rotuloDoBotao: string
}) {
  const [aberta, setAberta] = useState(false)
  const caixa = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!aberta) return
    const aoApontar = (evento: PointerEvent) => {
      const alvo = evento.target
      if (alvo instanceof Node && caixa.current?.contains(alvo)) return
      setAberta(false)
    }
    document.addEventListener('pointerdown', aoApontar)
    return () => document.removeEventListener('pointerdown', aoApontar)
  }, [aberta])

  // sem para onde ir, o campo é só leitura: um botão que abre lista vazia
  // promete uma escolha que não existe
  if (opcoes.length === 0) return <>{atual ?? vazio}</>

  return (
    <div className="rs__escolha" ref={caixa}>
      <button
        type="button"
        className="rs__escolha-atual"
        onClick={() => setAberta((v) => !v)}
        aria-expanded={aberta}
        aria-haspopup="listbox"
        title={rotuloDoBotao}
      >
        {atual ?? <span className="rs__vazio">{vazio}</span>}
        <i className="fas fa-chevron-down rs__escolha-seta" aria-hidden />
      </button>

      {aberta ? (
        <div className="rs__escolha-lista" role="listbox" aria-label={rotuloDoBotao}>
          {opcoes.map((o) => (
            <button
              key={o.id}
              type="button"
              role="option"
              aria-selected={false}
              className="rs__escolha-op"
              onClick={() => {
                setAberta(false)
                onEscolher(o.id)
              }}
            >
              {o.conteudo}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}

/** A opção "deixar sem responsável" na lista de pessoas. */
const SEM_RESPONSAVEL = '__sem-responsavel__'

export function ResumoDoItem({
  detail,
  rotuloDe,
  statusOpcoes,
  onTrocarStatus,
  prioridadeOpcoes,
  onTrocarPrioridade,
  etapaOpcoes,
  onTrocarEtapa,
  onVincularIniciativa,
  onTrocarResponsavel,
  responsavelOpcoes,
  responsavel,
  prioridade,
  etapa,
  nomeDe,
}: {
  detail: Detail
  /** O rótulo traduzido de uma linha de catálogo; a página já sabe resolver. */
  rotuloDe: (row: Catalogo | null | undefined) => string
  statusOpcoes: Catalogo[]
  onTrocarStatus: (statusId: string) => void
  prioridadeOpcoes: Catalogo[]
  onTrocarPrioridade: (priorityId: string) => void
  /** Só as etapas para onde o item PODE ir, pelo desenho do fluxo. */
  etapaOpcoes: { id: string; rotulo: string }[]
  onTrocarEtapa: (flowStepId: string) => void
  onVincularIniciativa: () => void
  /** null = deixar sem responsável. */
  onTrocarResponsavel: (memberId: string | null) => void
  /** Quem pode ser responsável (membros ativos do workspace). */
  responsavelOpcoes: { id: string; name: string }[]
  /** Resolvidos pela pagina, que ja tem os catalogos carregados. */
  responsavel: { name: string } | null
  prioridade: string
  etapa: string
  nomeDe: (memberId: string) => string
}) {
  const item = detail.item
  const pai = detail.breadcrumb?.filter(Boolean).at(-1) ?? null
  const checklists = detail.collab?.checklists ?? []
  const linhas = checklists.flatMap((c) => c.items ?? [])
  /*
    A MESMA regra da lista de Itens (`@orbind/core`): a linha vinculada está
    feita quando o item dela está num status terminal; a solta, quando marcada.
  */
  const linhaFeita = (l: (typeof linhas)[number]) =>
    linhaDeChecklistFeita(
      { isDone: l.isDone, linkedWorkItemId: l.linked?.id ?? null },
      l.linked ? { concluido: Boolean(l.linked.status?.isTerminal) } : null,
    )
  const feitas = linhas.filter(linhaFeita).length
  const comentarios = detail.collab?.comments ?? []

  const data = (iso: string | null | undefined) =>
    iso
      ? new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })
          .format(new Date(iso))
          .replace('.', '')
      : '—'

  const atrasado = Boolean(item.dueAt) && !item.completedAt && new Date(item.dueAt!).getTime() < Date.now()
  const statusAtual = statusOpcoes.find((s) => s.id === item.workItemStatusId) ?? null

  const [datasAbertas, setDatasAbertas] = useState(false)
  const caixaDeDatas = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!datasAbertas) return
    const aoApontar = (evento: PointerEvent) => {
      const alvo = evento.target
      if (alvo instanceof Node && caixaDeDatas.current?.contains(alvo)) return
      setDatasAbertas(false)
    }
    document.addEventListener('pointerdown', aoApontar)
    return () => document.removeEventListener('pointerdown', aoApontar)
  }, [datasAbertas])

  /*
    `wi-rs`, e não `rs`: a reunião do Escritório usa `.rs` como camada
    escura em tela cheia (reuniao.css), e com o CSS dela carregado o resumo
    virava aquela tela (SPEC-20261010-1707-resumo-do-item-no-painel-e-responsavel).
  */
  return (
    <div className="wi-rs">
      {/*
        De onde o item vem. Sem pai ele é AVULSO, e aqui mesmo se oferece o
        caminho de ligá-lo a uma iniciativa — é o lugar em que a falta aparece.
      */}
      <p className="rs__trilha">
        {pai ? (
          <>
            <i className="fas fa-sitemap" aria-hidden /> {pai.title}
          </>
        ) : (
          <>
            <i className="far fa-circle" aria-hidden /> Item avulso ·{' '}
            <button type="button" className="rs__link" onClick={onVincularIniciativa}>
              vincular a uma iniciativa
            </button>
          </>
        )}
      </p>

      <h2 className="rs__titulo">{item.title}</h2>

      <dl className="rs__campos">
        <div className="rs__campo">
          <dt>Status</dt>
          <dd>
            <CampoComLista
              rotuloDoBotao="Mudar o status"
              atual={
                statusAtual ? (
                  <StatusPill tone={statusAtual.tone ?? 'neutral'}>{rotuloDe(statusAtual)}</StatusPill>
                ) : null
              }
              vazio="Sem status"
              opcoes={statusOpcoes
                .filter((s) => s.id !== item.workItemStatusId)
                .map((s) => ({
                  id: s.id,
                  conteudo: <StatusPill tone={s.tone ?? 'neutral'}>{rotuloDe(s)}</StatusPill>,
                }))}
              onEscolher={onTrocarStatus}
            />
          </dd>
        </div>

        <div className="rs__campo">
          <dt>Responsável</dt>
          <dd>
            {/*
              Escolhe ali mesmo, como status e prioridade (SPEC-20261010-1707-resumo-do-item-no-painel-e-responsavel).
              Antes o "trocar" levava à edição do item — o Cristian só queria o
              painel.
            */}
            <CampoComLista
              rotuloDoBotao="Trocar o responsável"
              atual={
                responsavel ? (
                  <span className="rs__pessoa">
                    <span className="wi-avatar">{initialsOf(responsavel.name)}</span>
                    {responsavel.name}
                  </span>
                ) : null
              }
              vazio="Sem responsável"
              opcoes={[
                ...responsavelOpcoes
                  .filter((m) => m.id !== item.assigneeMemberId)
                  .map((m) => ({
                    id: m.id,
                    conteudo: (
                      <span className="rs__pessoa">
                        <span className="wi-avatar">{initialsOf(m.name)}</span>
                        {m.name}
                      </span>
                    ),
                  })),
                ...(item.assigneeMemberId
                  ? [{ id: SEM_RESPONSAVEL, conteudo: <span className="rs__vazio">Deixar sem responsável</span> }]
                  : []),
              ]}
              onEscolher={(id) => onTrocarResponsavel(id === SEM_RESPONSAVEL ? null : id)}
            />
          </dd>
        </div>

        <div className="rs__campo">
          <dt>Prazo</dt>
          <dd className={atrasado ? 'rs__atrasado' : undefined}>
            {/*
              O MESMO painel de datas da barra de propriedades do item (pedido
              do Cristian, 2026-10-03). Um seletor próprio aqui seria uma segunda
              regra de data — começo depois do fim, feriado, duração — que
              divergiria da primeira.
            */}
            <div className="rs__escolha" ref={caixaDeDatas}>
              <button
                type="button"
                className="rs__escolha-atual"
                onClick={() => setDatasAbertas((v) => !v)}
                aria-expanded={datasAbertas}
                title="Mudar as datas"
              >
                {data(item.dueAt)}
                {atrasado ? ' · atrasado' : ''}
                <i className="fas fa-chevron-down rs__escolha-seta" aria-hidden />
              </button>
              {datasAbertas ? <DatesPanel detail={detail} onClose={() => setDatasAbertas(false)} /> : null}
            </div>
          </dd>
        </div>

        <div className="rs__campo">
          <dt>Prioridade</dt>
          <dd>
            <CampoComLista
              rotuloDoBotao="Mudar a prioridade"
              atual={prioridade || null}
              opcoes={prioridadeOpcoes
                .filter((x) => x.id !== item.workItemPriorityId)
                .map((x) => ({ id: x.id, conteudo: rotuloDe(x) }))}
              onEscolher={onTrocarPrioridade}
            />
          </dd>
        </div>

        <div className="rs__campo">
          <dt>Etapa</dt>
          <dd>
            {/*
              Só as etapas para onde o fluxo deixa ir. Oferecer o catálogo
              inteiro seria oferecer saltos que o desenho não prevê.
            */}
            <CampoComLista
              rotuloDoBotao="Mudar a etapa"
              atual={etapa || null}
              opcoes={etapaOpcoes.map((e) => ({ id: e.id, conteudo: e.rotulo }))}
              onEscolher={onTrocarEtapa}
            />
          </dd>
        </div>
      </dl>

      <section className="rs__bloco">
        <h3>Descrição</h3>
        {item.description?.trim() ? (
          // texto puro: o resumo não é lugar de editar conteúdo rico
          <p className="rs__desc">{item.description.replace(/<[^>]*>/g, ' ').trim()}</p>
        ) : (
          <p className="rs__vazio">Sem descrição.</p>
        )}
      </section>

      <section className="rs__bloco">
        <h3>
          Subitens <em>{`${feitas}/${linhas.length}`}</em>
        </h3>
        {linhas.length > 0 ? (
          <ul className="rs__subitens">
            {linhas.slice(0, 8).map((l) => {
              const feita = linhaFeita(l)
              /*
                A linha VINCULADA não tem texto próprio (nasce com texto vazio
                ao escolher um item existente): o nome é o do item. Sem isso o
                resumo mostrava só os quadradinhos (bug de 2026-10-08).
              */
              return (
                <li key={l.id} className={feita ? 'rs__feito' : undefined}>
                  <i
                    className={feita ? 'fas fa-check-square' : 'far fa-square'}
                    role="img"
                    aria-label={feita ? 'Concluído' : 'Em aberto'}
                  />
                  {/* um bloco só: o `li` é flex, e código e título soltos virariam colunas */}
                  <span>
                    {l.linked ? (
                      <>
                        <b>{l.linked.code}</b> {l.linked.title}
                      </>
                    ) : (
                      l.text || <span className="rs__vazio">Sem texto</span>
                    )}
                  </span>
                </li>
              )
            })}
          </ul>
        ) : (
          <p className="rs__vazio">Sem subitens.</p>
        )}
      </section>

      <section className="rs__bloco">
        <h3>Atividade</h3>
        {comentarios.length > 0 ? (
          <ul className="rs__atividade">
            {comentarios.slice(-4).map((c) => (
              <li key={c.id}>
                <span className="wi-avatar">{initialsOf(nomeDe(c.createdBy))}</span>
                <span>
                  <b>{nomeDe(c.createdBy)}</b> {c.text}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="rs__vazio">Nada ainda.</p>
        )}
      </section>
    </div>
  )
}
